import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";
import { useApiQuery } from "../../hooks";
import {
  AI_VALIDATION_MODES,
  type AiValidationMode,
  type ApiConfig,
  type AppConfig,
  type Check,
  type CheckOutputCapture,
  type CheckSchema,
  HTTP_METHODS,
  type HttpMethod,
  JSON_PATH_ASSERTION_TYPES,
  type JsonPathAssertion,
  type JsonPathAssertionType,
  RETRY_STRATEGIES,
  type RetryStrategy,
  type TestVariable,
} from "../../types";
import { AppConfigSelect } from "../app-config-select";
import { TestSelect } from "../test-select";
import { VariableInput } from "../variable-input";
import { VariableMonacoEditor } from "../variable-monaco-editor";

export interface CheckDialogProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onSave: (data: CheckSchema) => void;
  data: CheckSchema | Check;
  setData: (data: CheckSchema | Check | null) => void;
  isProcessing: boolean;
}

interface ChecksListResponse {
  checks: Check[];
  total: number;
}

// Mirror the backend slugify used to auto-derive a check alias from its name.
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Helper to extract variables from app config
const extractAppConfigVariables = (config: AppConfig | null): TestVariable[] => {
  if (!config) return [];
  const variables: TestVariable[] = [];

  if (config.login_url) {
    variables.push({
      key: "login_url",
      value: config.login_url,
      type: "url",
      lifetime: "all",
      context: "app_config",
      is_sensitive: false,
    });
  }
  if (config.login_username) {
    variables.push({
      key: "login_username",
      value: config.login_username,
      type: "username",
      lifetime: "all",
      context: "app_config",
      is_sensitive: false,
    });
  }
  if (config.login_password) {
    variables.push({
      key: "login_password",
      value: config.login_password,
      type: "password",
      lifetime: "all",
      context: "app_config",
      is_sensitive: true,
    });
  }
  if (config.login_mfa_secret) {
    variables.push({
      key: "login_mfa_secret",
      value: config.login_mfa_secret,
      type: "sensitive",
      lifetime: "all",
      context: "app_config",
      is_sensitive: true,
    });
  }
  if (config.base_url) {
    variables.push({
      key: "base_url",
      value: config.base_url,
      type: "url",
      lifetime: "all",
      context: "app_config",
      is_sensitive: false,
    });
  }

  return variables;
};

const CheckDialog: React.FC<CheckDialogProps> = ({
  open,
  mode,
  onClose,
  onSave,
  data,
  setData,
  isProcessing,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | false>(false);
  const [headersText, setHeadersText] = useState<string>(() =>
    data.config?.headers ? JSON.stringify(data.config.headers, null, 2) : "",
  );
  const [bodyText, setBodyText] = useState<string>(() => data.config?.body || "");
  const [validationTab, setValidationTab] = useState<string>("basic");
  const [requestTab, setRequestTab] = useState<string>("body");

  // Fetch app configs to get variables
  const { data: apiConfig } = useApiQuery<ApiConfig>("/config");

  // Fetch global variables
  const { data: globalVariables } = useApiQuery<TestVariable[]>("/global-variables");

  // Fetch sibling checks in the same app config (for the dependency picker)
  const { data: siblingChecksResponse } = useApiQuery<ChecksListResponse>(
    data.app_config_id ? `/checks?app_config_id=${data.app_config_id}` : "/checks",
    {
      enabled: !!data.app_config_id,
    },
  );

  // Candidate dependency checks: same app config, excluding the check being edited
  const currentCheckId = "id" in data ? data.id : undefined;
  const dependencyCheckOptions = useMemo(
    () => (siblingChecksResponse?.checks ?? []).filter((c) => c.id !== currentCheckId),
    [siblingChecksResponse, currentCheckId],
  );
  const selectedDependencyChecks = useMemo(
    () => dependencyCheckOptions.filter((c) => (data.check_dependency_ids || []).includes(c.id)),
    [dependencyCheckOptions, data.check_dependency_ids],
  );

  // Get variables from selected app config
  const appConfigVariables = useMemo(() => {
    if (!apiConfig?.configs || !data.app_config_id) return [];
    const config = apiConfig.configs.find((c) => c.id === data.app_config_id);
    return extractAppConfigVariables(config || null);
  }, [apiConfig, data.app_config_id]);

  // Derive the variables each selected dependency check exposes.
  // Auto variables come from the dependency's alias namespace; mapped
  // variables come from its output_captures.
  const dependencyVariableGroups = useMemo(() => {
    return selectedDependencyChecks.map((dep) => {
      const alias = dep.alias?.trim() || slugify(dep.name) || "dependency";
      const auto = [`{{${alias}.status}}`, `{{${alias}.headers.*}}`, `{{${alias}.body}}`];
      const mapped = (dep.output_captures ?? [])
        .filter((c) => c.name.trim())
        .map((c) => `{{${c.name}}}`);
      return { checkId: dep.id, checkName: dep.name, alias, auto, mapped };
    });
  }, [selectedDependencyChecks]);

  // Flat list of dependency-sourced variables for the autocomplete.
  const dependencyVariables = useMemo<TestVariable[]>(() => {
    const vars: TestVariable[] = [];
    for (const group of dependencyVariableGroups) {
      for (const v of [...group.auto, ...group.mapped]) {
        // Strip the {{ }} wrapper — VariableInput expects raw keys.
        const key = v.replace(/^\{\{/, "").replace(/\}\}$/, "");
        vars.push({
          key,
          value: "",
          type: "custom",
          lifetime: "all",
          context: "test",
          is_sensitive: false,
        });
      }
    }
    return vars;
  }, [dependencyVariableGroups]);

  // Combine all variables: global > app_config > check-specific > dependency
  const allVariables = useMemo(() => {
    return [
      ...(globalVariables || []),
      ...appConfigVariables,
      ...(data.variables || []),
      ...dependencyVariables,
    ];
  }, [globalVariables, appConfigVariables, data.variables, dependencyVariables]);

  const title = mode === "create" ? "New Check" : "Edit Check";
  const buttonText = mode === "create" ? "Create" : "Save";
  const description =
    mode === "create"
      ? "Create a new API check for monitoring and validation."
      : "Edit your check configuration.";

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleConfigChange = (field: string, value: string) => {
    setData({
      ...data,
      config: {
        ...data.config,
        [field]: value,
      },
    });
  };

  const handleMethodChange = (event: SelectChangeEvent) => {
    setData({
      ...data,
      config: {
        ...data.config,
        method: event.target.value as HttpMethod,
      },
    });
  };

  const handleValidationChange = (field: string, value: string | number) => {
    setData({
      ...data,
      validation: {
        ...data.validation,
        ai_mode: data.validation?.ai_mode || "fast",
        [field]: value,
      },
    });
  };

  const handleAddJsonPathAssertion = () => {
    const newAssertion: JsonPathAssertion = {
      path: "",
      type: "equals",
      expected: "",
    };
    setData({
      ...data,
      validation: {
        ...data.validation,
        ai_mode: data.validation?.ai_mode || "fast",
        json_path_assertions: [...(data.validation?.json_path_assertions || []), newAssertion],
      },
    });
  };

  const handleRemoveJsonPathAssertion = (index: number) => {
    const assertions = [...(data.validation?.json_path_assertions || [])];
    assertions.splice(index, 1);
    setData({
      ...data,
      validation: {
        ...data.validation,
        ai_mode: data.validation?.ai_mode || "fast",
        json_path_assertions: assertions.length > 0 ? assertions : undefined,
      },
    });
  };

  const handleJsonPathAssertionChange = (
    index: number,
    field: keyof JsonPathAssertion,
    value: string,
  ) => {
    const assertions = [...(data.validation?.json_path_assertions || [])];
    assertions[index] = {
      ...assertions[index],
      [field]: value,
    };
    setData({
      ...data,
      validation: {
        ...data.validation,
        ai_mode: data.validation?.ai_mode || "fast",
        json_path_assertions: assertions,
      },
    });
  };

  const handleRetryStrategyChange = (event: SelectChangeEvent) => {
    setData({
      ...data,
      retry_strategy: event.target.value as RetryStrategy,
    });
  };

  const handleDependencyChecksChange = (_: React.SyntheticEvent, value: Check[]) => {
    setData({
      ...data,
      check_dependency_ids: value.map((c) => c.id),
    });
  };

  const handleAddOutputCapture = () => {
    const newCapture: CheckOutputCapture = { name: "", json_path: "" };
    setData({
      ...data,
      output_captures: [...(data.output_captures || []), newCapture],
    });
  };

  const handleRemoveOutputCapture = (index: number) => {
    const captures = [...(data.output_captures || [])];
    captures.splice(index, 1);
    setData({
      ...data,
      output_captures: captures,
    });
  };

  const handleOutputCaptureChange = (
    index: number,
    field: keyof CheckOutputCapture,
    value: string,
  ) => {
    const captures = [...(data.output_captures || [])];
    captures[index] = {
      ...captures[index],
      [field]: value,
    };
    setData({
      ...data,
      output_captures: captures,
    });
  };

  const handleAccordionChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? panel : false);
    };

  const isValid = data.name && data.app_config_id && data.config?.url && data.config?.method;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="center" mt={2}>
          {/* Basic Info */}
          <TextField
            autoFocus
            required
            id="name"
            name="name"
            label="Name"
            placeholder="Health check - Production API"
            size="small"
            type="text"
            fullWidth
            value={data.name}
            onChange={handleChange}
          />

          <TextField
            id="description"
            name="description"
            label="Description"
            placeholder="Monitors the health endpoint of our production API..."
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            type="text"
            fullWidth
            value={data.description || ""}
            onChange={handleChange}
          />

          <AppConfigSelect
            label="App Configuration"
            value={data.app_config_id || null}
            onChange={(id) =>
              setData({
                ...data,
                app_config_id: id || "",
              })
            }
            onConfigChange={(config) => {
              // Auto-populate URL with {{base_url}} variable if URL is empty
              if (config?.base_url && !data.config?.url) {
                setData({
                  ...data,
                  app_config_id: config.id,
                  config: { ...data.config, url: "{{base_url}}" },
                });
              }
            }}
            size="small"
            helperText={
              data.app_config_id
                ? "Variables from this config are available for substitution"
                : "Select an app config to enable variable substitution"
            }
          />

          <TextField
            id="alias"
            name="alias"
            label="Alias"
            placeholder={data.name ? slugify(data.name) : "check_alias"}
            size="small"
            type="text"
            fullWidth
            value={data.alias || ""}
            onChange={handleChange}
            helperText="Namespace for response variables exposed to dependent checks (e.g. {{alias.status}}). Auto-derived from the name when empty."
          />

          {/* API Configuration */}
          <Typography variant="subtitle2" fontWeight={600} mt={2}>
            API Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 3 }}>
              <Select
                fullWidth
                size="small"
                value={data.config?.method || "GET"}
                onChange={handleMethodChange}
              >
                {HTTP_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ xs: 9 }}>
              <VariableInput
                required
                id="url"
                label="URL"
                placeholder="https://api.example.com/health"
                size="small"
                value={data.config?.url || ""}
                onChange={(value) => handleConfigChange("url", value)}
                variables={allVariables}
                helperText="Supports variable substitution: {{variable_name}}. Dependency checks also expose {{alias.status}}, {{alias.body}} and captured outputs."
              />
            </Grid>
          </Grid>

          {/* Request Body / Headers Tabs */}
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
            <TabContext value={requestTab}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  onChange={(_, newValue) => setRequestTab(newValue)}
                  aria-label="Request configuration tabs"
                >
                  <Tab label="Body" value="body" />
                  <Tab label="Headers" value="headers" />
                </TabList>
              </Box>

              <TabPanel value="body" sx={{ p: 2 }}>
                <VariableMonacoEditor
                  placeholder='{"key": "value", "user": "{{username}}"}'
                  language="json"
                  height={150}
                  value={bodyText}
                  onChange={(value) => {
                    setBodyText(value);
                    handleConfigChange("body", value);
                  }}
                  variables={allVariables}
                  helperText="Request body (JSON or plain text). Use {{variable_name}} for substitution."
                />
              </TabPanel>

              <TabPanel value="headers" sx={{ p: 2 }}>
                <VariableMonacoEditor
                  placeholder='{"Authorization": "Bearer {{api_token}}", "Content-Type": "application/json"}'
                  language="json"
                  height={150}
                  value={headersText}
                  onChange={(value) => {
                    setHeadersText(value);
                    try {
                      const headers = value ? JSON.parse(value) : undefined;
                      setData({
                        ...data,
                        config: {
                          ...data.config,
                          headers,
                        },
                      });
                    } catch {
                      // Allow invalid JSON while typing - store raw text
                    }
                  }}
                  variables={allVariables}
                  helperText="Optional. JSON object with request headers. Use {{variable_name}} for substitution."
                />
              </TabPanel>
            </TabContext>
          </Box>

          {/* Validation Rules */}
          <Typography variant="subtitle2" fontWeight={600} mt={2}>
            Validation Rules
          </Typography>

          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
            <TabContext value={validationTab}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  onChange={(_, newValue) => setValidationTab(newValue)}
                  aria-label="Validation rules tabs"
                >
                  <Tab
                    label={`Basic${data.validation?.expected_status_code ? ` (${data.validation.expected_status_code})` : ""}`}
                    value="basic"
                  />
                  <Tab
                    label={`Advanced${(data.validation?.json_path_assertions?.length || 0) > 0 ? ` (${data.validation?.json_path_assertions?.length})` : ""}`}
                    value="advanced"
                  />
                  <Tab label={`AI${data.validation?.ai_validation ? " (✓)" : ""}`} value="ai" />
                </TabList>
              </Box>

              {/* Basic Tab: Status Code & Body Contains */}
              <TabPanel value="basic" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    id="expected_status_code"
                    label="Expected Status Code"
                    placeholder="200"
                    size="small"
                    type="number"
                    fullWidth
                    value={data.validation?.expected_status_code || ""}
                    onChange={(e) =>
                      handleValidationChange(
                        "expected_status_code",
                        e.target.value ? parseInt(e.target.value, 10) : "",
                      )
                    }
                    helperText="Expected HTTP status code (e.g., 200, 201, 204)"
                  />

                  <VariableInput
                    id="expected_body_contains"
                    label="Response Body Contains"
                    placeholder="success"
                    size="small"
                    value={data.validation?.expected_body_contains || ""}
                    onChange={(value) => handleValidationChange("expected_body_contains", value)}
                    variables={allVariables}
                    helperText="Text that must be present in the response body. Use {{variable}} for substitution."
                  />
                </Stack>
              </TabPanel>

              {/* Advanced Tab: JSONPath Assertions */}
              <TabPanel value="advanced" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Add JSONPath assertions to validate specific fields in the request or response.
                    Use paths like <code>$.response.body.success</code> or{" "}
                    <code>$.response.headers.Content-Type</code>
                  </Typography>

                  {(data.validation?.json_path_assertions || []).map((assertion, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        backgroundColor: "background.default",
                      }}
                    >
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            Assertion {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveJsonPathAssertion(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>

                        <TextField
                          label="JSONPath"
                          placeholder="$.response.body.data.id"
                          size="small"
                          fullWidth
                          value={assertion.path}
                          onChange={(e) =>
                            handleJsonPathAssertionChange(index, "path", e.target.value)
                          }
                          helperText="JSONPath expression (e.g., $.response.body.status)"
                        />

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 4 }}>
                            <Select
                              fullWidth
                              size="small"
                              value={assertion.type}
                              onChange={(e) =>
                                handleJsonPathAssertionChange(
                                  index,
                                  "type",
                                  e.target.value as JsonPathAssertionType,
                                )
                              }
                            >
                              {JSON_PATH_ASSERTION_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            {assertion.type !== "exists" && (
                              <TextField
                                label="Expected Value"
                                placeholder={
                                  assertion.type === "contains"
                                    ? "substring to find"
                                    : "exact value"
                                }
                                size="small"
                                fullWidth
                                value={assertion.expected || ""}
                                onChange={(e) =>
                                  handleJsonPathAssertionChange(index, "expected", e.target.value)
                                }
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Stack>
                    </Box>
                  ))}

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddJsonPathAssertion}
                    size="small"
                  >
                    Add JSONPath Assertion
                  </Button>
                </Stack>
              </TabPanel>

              {/* AI Tab: AI Validation */}
              <TabPanel value="ai" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <VariableInput
                    id="ai_validation"
                    label="AI Validation Prompt"
                    placeholder="Verify that the response contains a valid user object with id and email fields"
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={data.validation?.ai_validation || ""}
                    onChange={(value) => handleValidationChange("ai_validation", value)}
                    variables={allVariables}
                    helperText="Natural language description of what the AI should validate. Use {{variable}} for substitution."
                  />

                  {data.validation?.ai_validation && (
                    <Select
                      fullWidth
                      size="small"
                      value={data.validation?.ai_mode || "fast"}
                      onChange={(e) =>
                        handleValidationChange("ai_mode", e.target.value as AiValidationMode)
                      }
                    >
                      {AI_VALIDATION_MODES.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                          {mode.charAt(0).toUpperCase() + mode.slice(1)} Mode
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Stack>
              </TabPanel>
            </TabContext>
          </Box>

          {/* Retry Configuration */}
          <Accordion
            expanded={expandedSection === "retry"}
            onChange={handleAccordionChange("retry")}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Retry Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  (
                  {(data.retry_strategy || "none").charAt(0).toUpperCase() +
                    (data.retry_strategy || "none").slice(1)}
                  )
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Select
                      fullWidth
                      size="small"
                      value={data.retry_strategy || "none"}
                      onChange={handleRetryStrategyChange}
                    >
                      {RETRY_STRATEGIES.map((strategy) => (
                        <MenuItem key={strategy} value={strategy}>
                          {strategy.charAt(0).toUpperCase() + strategy.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      id="max_retries"
                      label="Max Retries"
                      size="small"
                      type="number"
                      fullWidth
                      value={data.max_retries ?? 3}
                      onChange={(e) =>
                        setData({
                          ...data,
                          max_retries: parseInt(e.target.value, 10) || 3,
                        })
                      }
                      disabled={data.retry_strategy === "none"}
                    />
                  </Grid>
                </Grid>

                {data.retry_strategy !== "none" && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        id="retry_delay_seconds"
                        label="Retry Delay"
                        size="small"
                        type="number"
                        fullWidth
                        value={data.retry_delay_seconds ?? 1}
                        onChange={(e) =>
                          setData({
                            ...data,
                            retry_delay_seconds: parseFloat(e.target.value) || 1,
                          })
                        }
                        InputProps={{
                          endAdornment: <InputAdornment position="end">sec</InputAdornment>,
                        }}
                      />
                    </Grid>
                    {data.retry_strategy === "exponential" && (
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          id="backoff_multiplier"
                          label="Backoff Multiplier"
                          size="small"
                          type="number"
                          fullWidth
                          value={data.backoff_multiplier ?? 2}
                          onChange={(e) =>
                            setData({
                              ...data,
                              backoff_multiplier: parseFloat(e.target.value) || 2,
                            })
                          }
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Test Dependency Configuration */}
          <Accordion
            expanded={expandedSection === "dependency"}
            onChange={handleAccordionChange("dependency")}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Test Dependency
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.test_dependency_id ? "(Configured)" : "(None)"}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TestSelect
                  label="Test Dependency (Optional)"
                  placeholder="Search for a test..."
                  value={data.test_dependency_id || null}
                  onChange={(testId) =>
                    setData({
                      ...data,
                      test_dependency_id: testId || undefined,
                    })
                  }
                  appConfigId={data.app_config_id}
                  size="small"
                  helperText="Run this test before the check to generate auth tokens"
                />

                {data.test_dependency_id && (
                  <TextField
                    id="dependency_cache_ttl_seconds"
                    label="Cache TTL (seconds)"
                    size="small"
                    type="number"
                    fullWidth
                    value={data.dependency_cache_ttl_seconds || ""}
                    onChange={(e) =>
                      setData({
                        ...data,
                        dependency_cache_ttl_seconds: e.target.value
                          ? parseInt(e.target.value, 10)
                          : undefined,
                      })
                    }
                    InputProps={{
                      endAdornment: <InputAdornment position="end">sec</InputAdornment>,
                    }}
                    helperText="Leave empty to auto-detect from JWT expiration"
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Check Dependencies (chaining) */}
          <Accordion
            expanded={expandedSection === "dependencies"}
            onChange={handleAccordionChange("dependencies")}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Dependencies
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(data.check_dependency_ids?.length || 0) > 0
                    ? `(${data.check_dependency_ids?.length})`
                    : "(None)"}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Checks that must run before this one inside a schedule. Their captured outputs
                  become available as variables here.
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={dependencyCheckOptions}
                  value={selectedDependencyChecks}
                  onChange={handleDependencyChecksChange}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={!data.app_config_id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        data.app_config_id
                          ? "Select dependency checks..."
                          : "Select an app config first"
                      }
                      size="small"
                      helperText={`${selectedDependencyChecks.length} dependency check(s) selected`}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                        sx={{ fontSize: "0.75rem", height: 22 }}
                      />
                    ))
                  }
                />

                {/* Available variables exposed by selected dependency checks */}
                {dependencyVariableGroups.length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      backgroundColor: "background.default",
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Available dependency variables
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Reference these as <code>{"{{...}}"}</code> in this check's URL, headers, body
                      or validation.
                    </Typography>
                    <Stack spacing={1.5} mt={1.5}>
                      {dependencyVariableGroups.map((group) => (
                        <Box key={group.checkId}>
                          <Typography variant="body2" fontWeight={600}>
                            {group.checkName}{" "}
                            <Typography component="span" variant="caption" color="text.secondary">
                              (alias: {group.alias})
                            </Typography>
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.5}>
                            {group.auto.map((v) => (
                              <Chip
                                key={v}
                                label={v}
                                size="small"
                                variant="outlined"
                                sx={{ fontFamily: "monospace", fontSize: "0.7rem", height: 22 }}
                              />
                            ))}
                            {group.mapped.map((v) => (
                              <Chip
                                key={v}
                                label={v}
                                size="small"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.7rem",
                                  height: 22,
                                  backgroundColor: "info.light",
                                  color: "info.contrastText",
                                }}
                              />
                            ))}
                            {group.mapped.length === 0 && (
                              <Typography variant="caption" color="text.secondary">
                                No output captures defined on this check.
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Output Captures (chaining) */}
          <Accordion
            expanded={expandedSection === "outputCaptures"}
            onChange={handleAccordionChange("outputCaptures")}
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Output Captures
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(data.output_captures?.length || 0) > 0
                    ? `(${data.output_captures?.length})`
                    : "(None)"}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Extract named values from this check's response so dependent checks can reference
                  them as <code>{"{{name}}"}</code>. Use JSONPath like{" "}
                  <code>$.response.body.data.id</code> or{" "}
                  <code>$.response.headers.authorization</code>.
                </Typography>

                {(data.output_captures || []).map((capture, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      backgroundColor: "background.default",
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Capture {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveOutputCapture(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 5 }}>
                          <TextField
                            label="Variable Name"
                            placeholder="claim_id"
                            size="small"
                            fullWidth
                            value={capture.name}
                            onChange={(e) =>
                              handleOutputCaptureChange(index, "name", e.target.value)
                            }
                            helperText="Referenced downstream as {{name}}"
                          />
                        </Grid>
                        <Grid size={{ xs: 7 }}>
                          <TextField
                            label="JSONPath"
                            placeholder="$.response.body.data.claim_id"
                            size="small"
                            fullWidth
                            value={capture.json_path}
                            onChange={(e) =>
                              handleOutputCaptureChange(index, "json_path", e.target.value)
                            }
                            helperText="JSONPath against the {request, response} context"
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Box>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddOutputCapture}
                  size="small"
                >
                  Add Output Capture
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Config */}
          <Typography variant="subtitle2" fontWeight={600} mt={2}>
            Config
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 6 }}>
              <TextField
                id="timeout_ms"
                label="Timeout"
                size="small"
                type="number"
                fullWidth
                value={data.timeout_ms ?? 30000}
                onChange={(e) =>
                  setData({
                    ...data,
                    timeout_ms: parseInt(e.target.value, 10) || 30000,
                  })
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                }}
                helperText="Request timeout in milliseconds"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                select
                id="follow_redirects"
                label="Follow Redirects"
                size="small"
                fullWidth
                value={(data.config?.follow_redirects ?? true) ? "yes" : "no"}
                onChange={(e) =>
                  setData({
                    ...data,
                    config: {
                      ...data.config,
                      follow_redirects: e.target.value === "yes",
                    },
                  })
                }
                helperText="Automatically follow HTTP redirects"
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={!isValid || isProcessing}
          loading={isProcessing}
          onClick={() => {
            if (data) {
              const payload: CheckSchema = {
                name: data.name,
                description: data.description,
                app_config_id: data.app_config_id,
                check_type: "api",
                config: {
                  url: data.config.url,
                  method: data.config.method,
                  headers: data.config.headers,
                  body: data.config.body,
                  follow_redirects: data.config.follow_redirects ?? true,
                },
                validation: data.validation,
                variables: data.variables || [],
                retry_strategy: data.retry_strategy || "none",
                max_retries: data.max_retries ?? 3,
                retry_delay_seconds: data.retry_delay_seconds ?? 1,
                backoff_multiplier: data.backoff_multiplier ?? 2,
                timeout_ms: data.timeout_ms ?? 30000,
                test_dependency_id: data.test_dependency_id || undefined,
                dependency_cache_ttl_seconds: data.dependency_cache_ttl_seconds || undefined,
                alias: data.alias?.trim() || undefined,
                check_dependency_ids: data.check_dependency_ids || [],
                output_captures: (data.output_captures || []).filter(
                  (c) => c.name.trim() && c.json_path.trim(),
                ),
              };
              onSave(payload);
            }
          }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckDialog;
